import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'

// Components.
import Layout from 'components/Layout/Layout'
import LoadingSpinner from 'components/LoadingSpinner'

// Constants.
import { decodeHtml } from 'lib/utils/decode'
import { revalidate } from 'lib/constants/revalidate'
import apiService from 'lib/services/pixels-headless/common'
import TextSearchBar from 'components/TextSearchBar'
import EventList from 'components/Section/EventList'

/**
 * Get common data for 404 page.
 */
export async function getStaticProps({ locale = false }) {
  const data = await apiService.getGlobals(locale)

  return {
    props: {
      data,
    },
    revalidate,
  }
}

const PageList = ({ pages = [] }) => (
  <>
    {!!pages.length &&
      pages.map(({ title, id, description, slug }) => (
        <div key={`hit-header-${id}`} className={`search-page--pages-content`}>
          <h6 className="search-page--pages-title">{decodeHtml(title)}</h6>
          <p className="search-page--pages-content-text">
            {decodeHtml(description)}
          </p>
          <Link href={slug} passHref>
            <p className="search-page--pages-content-button">Näytä lisää</p>
          </Link>
        </div>
      ))}
  </>
)

const EventHeader = ({ count }) => {
  switch (true) {
    case count >= 100:
      return (
        <>
          <span className="pink-text">99+</span>&nbsp;Tapahtumaa
        </>
      )
    case count > 0 && count < 100:
      return (
        <>
          <span className="pink-text">{count}</span>&nbsp;Tapahtumaa
        </>
      )
    default:
      return 'Tapahtumia ei löytynyt hakuehdoilla'
  }
}
const PageHeader = ({ count }) => {
  switch (true) {
    case count >= 100:
      return (
        <>
          <span className="pink-text">99+</span>&nbsp;Muuta sisältöä
        </>
      )
    case count > 0 && count < 100:
      return (
        <>
          <span className="pink-text">{count}</span>&nbsp;Muuta sisältöä
        </>
      )
    default:
      return 'Muuta sisältöä ei löytynyt'
  }
}

export default function SearchPage({ data, preview }) {
  const router = useRouter()
  const { search, page } = router.query

  const [value, changeValue] = useState(search || null)
  const [loading, changeLoading] = useState(false)
  const [hits, changeHits] = useState([])
  const [pageHits, changePages] = useState([])
  const [visibleEvents, changeVisibleEvents] = useState(9)

  const NextButton = () => {
    if (hits.length > visibleEvents) {
      return (
        <button
          role="button"
          className="btn btn--primary"
          onClick={() => {
            changeVisibleEvents(visibleEvents + 9)
          }}
        >
          <span>Näytä lisää tapahtumia</span>
        </button>
      )
    }
    if (hits.length <= visibleEvents && hits.length > 99) {
      return (
        <button
          role="button"
          className="btn btn--primary"
          onClick={() => {
            router.push({
              pathname: '/haku',
              query: { search: value, page: Number(page) + 1 },
            })
          }}
        >
          <span>Hae seuraava sivu</span>
        </button>
      )
    }
    return null
  }

  const handleSearch = async (val = '') => {
    if (value === null) return
    changeLoading(true)
    const { events, pages } = await fetch('/api/search', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        searchTerms: {
          search: encodeURIComponent(val) || '',
          page,
          per_page: 100,
        },
      }),
    })
      .then((r) => r.json())
      .catch(() => [])

    changeHits(events || [])
    changePages(pages || [])
    changeLoading(false)
  }

  useEffect(() => {
    changeValue(search)
    handleSearch(search)
    changeVisibleEvents(9)
  }, [router.query])

  const shownEvents = hits.slice(0, visibleEvents)

  return (
    <Layout data={data} preview={preview}>
      <div className="container search-page">
        <div className="">
          <h4 className="pink-text search-page--text">
            Hakutulokset haulle: {search}
          </h4>
          <div className="search-page--text">
            <TextSearchBar
              callback={(obj) => changeValue(obj.search || '')}
              origValue={search}
            ></TextSearchBar>
            <button
              className="btn btn--primary search-page--button"
              onClick={() =>
                router.replace({
                  pathname: '/haku',
                  query: { search: value, page: 1 },
                })
              }
            >
              Hae
            </button>
          </div>
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            <div className="search-page--events">
              <h5 className="search-page--title">
                <EventHeader count={hits.length} />
              </h5>
              <EventList
                section={{
                  events: shownEvents,
                  showButton: false,
                }}
              />
              <NextButton />
            </div>
            <div className="search-page--pages">
              <h5 className="search-page--title">
                <PageHeader count={pageHits.length} />
              </h5>
              <div>
                <PageList pages={pageHits} />
              </div>
            </div>
          </>
        )}
      </div>
    </Layout>
  )
}
